import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { InfoOutlined } from "@mui/icons-material";
import { Theme, useTheme } from "@mui/material";
import Image from "next/image";
import Link from "next/link";

import RaidProgressBar from "~/components/common/raid/raid-progress-bar";
import { TreasuryRaidLink } from "~/extensions/hojokin";
import { engFont } from "~/lib/client/fonts";
import { useRaidDetailContext } from "~/pages/raid/[id]";

const SpRaidDetail = () => {
  const { raid, generatedAt } = useRaidDetailContext();
  const theme = useTheme();
  const achievedRate = Math.floor(
    (raid.category.accumulatedPoint / raid.achievementPoint) * 100
  );

  return (
    <main>
      <article>
        <Image
          src={raid.bannerImageUrl}
          alt={raid.title}
          width={600}
          height={200}
          sizes="100vw"
          style={{
            width: "100%",
            height: "auto",
          }}
        />
        <div css={raidMainVisualStyle}></div>
        <FaveContentContainer muiTheme={theme}>
          <h1 css={raidMainInfoStyle} className={engFont.className}>
            <div css={raidStagePointStyle}>
              <div css={stageStyle}>{raid.stage.ordinal()} Stage</div>
              <div css={raidPointStyle}>
                <div css={goalPointStyle}>GOAL</div>
                <div css={raidAchievementPointStyle}>
                  {raid.achievementPoint.toLocaleString()}
                </div>
              </div>
            </div>
            <p css={raidTitleStyle}>{raid.title}</p>
          </h1>
          <RaidProgressBar
            achievedRate={achievedRate}
            point={raid.category.accumulatedPoint.toLocaleString()}
          />
        </FaveContentContainer>
        <p css={supplementaryInfoStyle(theme)}>
          <InfoOutlined />
          このページは1時間毎に更新されます。(最終更新: {generatedAt})
        </p>
        <RaidDescriptionContainer muiTheme={theme}>
          <div dangerouslySetInnerHTML={{ __html: raid.description }} />
        </RaidDescriptionContainer>
        <LinkToFavePageContainer>
          <Link href={`/category/${raid.category.id}`}>
            <span>{raid.category.name}</span>のファンページへ
          </Link>
        </LinkToFavePageContainer>
      </article>
      <hr />
      <h2
        css={css`
          padding-left: 2rem;
        `}
      >
        Archived Projects
      </h2>
      <ArchivedRaidsContainer muiTheme={theme}>
        {raid.category.raids
          .sort((a, b) => b.stage - a.stage)
          .map((raid) => {
            return (
              <ArchivedRaid key={raid.id}>
                <Link href={`/raid/${raid.id}`}>
                  <Image
                    src={raid.bannerImageUrl}
                    alt={raid.title}
                    width={600}
                    height={200}
                    sizes="100vw"
                    style={{
                      width: "100%",
                      height: "auto",
                    }}
                  />
                </Link>
                <div css={archivedRaidStageStyle}>
                  {raid.stage.ordinal()} Stage
                </div>
                <div css={archivedRaidTitleStyle}>{raid.title}</div>
              </ArchivedRaid>
            );
          })}
      </ArchivedRaidsContainer>
      <TreasuryRaidLink />
    </main>
  );
};

const raidMainVisualStyle = css``;

const FaveContentContainer = styled.div<{
  muiTheme: Theme;
}>`
  padding: 2rem;
  background: ${({ muiTheme }) => muiTheme.palette.background.paper};
`;

const raidMainInfoStyle = css``;

const stageStyle = css`
  font-weight: 700;
  font-size: 24px;
`;

const raidStagePointStyle = css`
  display: flex;
  flex-flow: row;
  align-items: center;
  justify-content: space-between;
`;

const raidPointStyle = css`
  display: flex;
  flex-flow: column;
  align-items: center;
`;

const goalPointStyle = css`
  font-size: 11px;
  font-weight: 400;
`;

const raidAchievementPointStyle = css`
  font-size: 18px;
  font-weight: 700;
`;

const raidTitleStyle = css`
  font-size: 26px;
  font-weight: 700;
`;

const RaidDescriptionContainer = styled.div<{ muiTheme: Theme }>`
  padding: 1rem;
  color: ${({ muiTheme }) => muiTheme.palette.text.primary};
  white-space: pre-wrap;
  word-break: break-word;

  h2 {
    margin: 0;
  }

  p {
    margin: 0;
  }

  ul {
    margin: 0;
  }

  a {
    color: ${({ muiTheme }) => muiTheme.palette.primary.main};
    text-decoration: underline;
  }
`;

const LinkToFavePageContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  font-weight: 600;
  padding-right: 2rem;

  a {
    font-size: 14px;
    text-decoration: underline;
  }
`;

const ArchivedRaidsContainer = styled.div<{ muiTheme: Theme }>`
  width: 100%;
  padding-bottom: 2rem;
  border-bottom: 1px solid ${({ muiTheme }) => muiTheme.palette.divider};
`;

const ArchivedRaid = styled.div`
  padding: 2rem;
`;

const archivedRaidStageStyle = css`
  font-size: 12px;
  font-weight: 700;
`;

const archivedRaidTitleStyle = css`
  font-size: 20px;
  font-weight: 700;
`;

const supplementaryInfoStyle = (theme: Theme) => css`
  display: flex;
  flex-flow: row;
  justify-content: flex-end;
  text-align: center;
  align-items: center;
  color: ${theme.palette.text.secondary};
  font-size: 0.8rem;
  gap: 0.25rem;
  padding-right: 0.5rem;
`;

export default SpRaidDetail;
